import { parseAsInteger, parseAsString } from 'nuqs';
import { useEffect, useId, useMemo, useState } from 'react';

import { useDebounce } from '@/hooks/useDebounce';
import { useQueryStateWithoutSuspense } from '@/hooks/useQueryStateWithoutSuspense';

interface UseUrlSearchProps {
  searchKey?: string;
  delay?: number;
  resetPageOnSearch?: boolean;
}
export const useUrlSearch = (props?: UseUrlSearchProps) => {
  const { searchKey, delay = 500, resetPageOnSearch = true } = props || {};
  // Generate unique key for each hook instance
  const uniqueId = useId();

  const defaultSearchKey = useMemo(() => `q-${uniqueId.replace(/:/g, '')}`, [uniqueId]);

  const existingSearchKey = searchKey || defaultSearchKey;

  const [searchParam, setSearchParam] = useQueryStateWithoutSuspense(existingSearchKey, parseAsString.withDefault(''));
  const [pageParam, setPageParam] = useQueryStateWithoutSuspense('page', parseAsInteger.withDefault(1));

  const [searchValue, setSearchValue] = useState(searchParam);
  const debouncedSearchValue = useDebounce(searchValue, delay);

  useEffect(() => {
    if (debouncedSearchValue !== searchParam) {
      setSearchParam(debouncedSearchValue.trim() || null);

      if (resetPageOnSearch) {
        setPageParam(1);
      }
    }
  }, [debouncedSearchValue, resetPageOnSearch, searchParam, setSearchParam, setPageParam]);

  useEffect(() => {
    if (searchParam !== searchValue) {
      setSearchValue(searchParam);
    }
  }, [searchParam]);

  return {
    searchValue,
    setSearchValue,
    searchParam,
    pageParam,
    setPageParam,
  };
};
